import type { ShopperBasketsV2, ShopperSearch } from '@/scapi';
import { pickAnalyticsProductFields } from '@/lib/analytics/data-layer-mappers';
import type { SCAPIShopperSearchSchemas } from '@/types/scapi';

const DEFAULT_COLOR_ID = 'color';

type SearchHit = ShopperSearch.schemas['ProductSearchHit'] | SCAPIShopperSearchSchemas['ProductSearchHit'];

function getSelectedColorVariant(product: SearchHit, selectedColorValue: string | null | undefined) {
    if (!product.variants?.length || !selectedColorValue) {
        return undefined;
    }

    return product.variants.find((variant) => {
        const values = (variant as { variationValues?: Record<string, string> }).variationValues;
        return values?.[DEFAULT_COLOR_ID] === selectedColorValue;
    });
}

/** Bundle search hits: ATC / cart matching uses the parent bundle id, never a child. */
function isPlpSearchHitBundle(product: SearchHit): boolean {
    return Boolean(product.hitType === 'bundle' || product.productType?.bundle);
}

/**
 * Resolves a cart line for a product grid hit by matching `productId` on basket items
 * to the hit’s `productId`, `representedProduct.id`, or a selected color variant.
 *
 * Bundles (multipack and assortment) match the parent id first — `representedProduct` is a
 * child and must not steal the match (or ATC would target / show a child SKU).
 */
export function findBasketLineForSearchHit(
    product: SearchHit,
    selectedColorValue: string | null | undefined,
    productItems: ShopperBasketsV2.schemas['ProductItem'][] | undefined
): ShopperBasketsV2.schemas['ProductItem'] | undefined {
    if (!productItems?.length) {
        return undefined;
    }
    const byId = (pid: string) => productItems.find((i) => i.productId === pid);

    if (isPlpSearchHitBundle(product) && product.productId) {
        return byId(product.productId);
    }

    if (product.variants?.length && selectedColorValue) {
        const variant = getSelectedColorVariant(product, selectedColorValue);
        if (variant?.productId) {
            const line = byId(variant.productId);
            if (line) {
                return line;
            }
        }
    }
    if (product.representedProduct?.id) {
        const line = byId(product.representedProduct.id);
        if (line) {
            return line;
        }
    }
    if (product.productId) {
        return byId(product.productId);
    }
    return undefined;
}

/**
 * Resolves the Commerce product ID to add for a PLP / tile quick-add (variant when color is selected, else represented
 * or master id). Mirrors {@link findBasketLineForSearchHit} but does not require the basket.
 *
 * Bundles: always the hit `productId` (bundle parent). Preferring `representedProduct.id` would add a child.
 */
export function getPlpAddToCartProductId(
    product: SearchHit,
    selectedColorValue: string | null | undefined
): string | undefined {
    if (isPlpSearchHitBundle(product)) {
        return product.productId ?? undefined;
    }
    if (product.variants?.length && selectedColorValue) {
        const variant = getSelectedColorVariant(product, selectedColorValue);
        if (variant?.productId) {
            return variant.productId;
        }
    }
    if (product.representedProduct?.id) {
        return product.representedProduct.id;
    }
    return product.productId ?? undefined;
}

/**
 * Builds the richest product payload available from a PLP/search hit for GA4
 * commerce events (add_to_cart, select_item, etc.).
 */
export function getPlpProductToTrack(
    product: ShopperSearch.schemas['ProductSearchHit'],
    selectedColorValue: string | null | undefined,
    quantity: number
): ShopperSearch.schemas['ProductSearchHit'] & { quantity: number } {
    const productId = getPlpAddToCartProductId(product, selectedColorValue);
    const variant = getSelectedColorVariant(product, selectedColorValue);
    const represented =
        product.representedProduct && typeof product.representedProduct === 'object' ? product.representedProduct : {};
    const analyticsFields = {
        ...pickAnalyticsProductFields(represented),
        ...pickAnalyticsProductFields(product),
        ...pickAnalyticsProductFields(variant),
    };

    return {
        ...analyticsFields,
        ...product,
        ...(variant ?? {}),
        ...(productId ? { productId, id: productId } : {}),
        quantity,
        price: variant?.price ?? product.price,
        product: analyticsFields,
    };
}

/**
 * Product-set search hits need configuration on the PDP; they are not single-SKU add-to-cart.
 * Bundles (multipack and assortment) are excluded — they add like a simple SKU via the parent id.
 */
export function isPlpSearchHitBundleOrSet(product: SearchHit): boolean {
    return Boolean(product.hitType === 'set' || product.productType?.set);
}
